'use client';

import { createContext, useContext, useEffect, useState, useCallback } from 'react';
import { createClient } from '@/lib/supabase/client';
import type { UserRole } from '@/lib/rbac';

export interface Region {
  id: string;
  code: string;
  nom: string;
  description: string;
  chef_lieu: string;
  is_siege_direction: boolean;
  is_active: boolean;
}

interface UserProfile {
  id: string;
  email: string;
  full_name: string;
  role: UserRole;
  avatar_url: string;
  is_active: boolean;
  region_id: string | null;
  created_at: string;
  updated_at: string;
  region?: Region | null;
}

interface ModulePermission {
  id: string;
  role: UserRole;
  module: string;
  can_view: boolean;
  can_create: boolean;
  can_edit: boolean;
  can_delete: boolean;
}

interface AuthContextType {
  user: any;
  session: any;
  loading: boolean;
  profile: UserProfile | null;
  permissions: ModulePermission[];
  userRole: UserRole | null;
  userRegion: Region | null;
  isDirection: boolean;
  signUp: (email: string, password: string, metadata?: any) => Promise<any>;
  signIn: (email: string, password: string) => Promise<any>;
  signOut: () => Promise<void>;
  getCurrentUser: () => Promise<any>;
  isEmailVerified: () => boolean;
  getUserProfile: () => Promise<UserProfile | null>;
  refreshProfile: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType>({} as AuthContextType);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }: { children: React.ReactNode }) => {
  const [user, setUser] = useState<any>(null);
  const [session, setSession] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [permissions, setPermissions] = useState<ModulePermission[]>([]);
  const supabase = createClient();

  const loadProfileAndPermissions = useCallback(async (userId: string) => {
    try {
      const [profileRes, permsRes] = await Promise.all([
        supabase
          .from('user_profiles')
          .select('*, region:regions(*)')
          .eq('id', userId)
          .single(),
        supabase.from('module_permissions').select('*').order('module'),
      ]);
      if (profileRes.data) setProfile(profileRes.data as UserProfile);
      if (permsRes.data) setPermissions(permsRes.data as ModulePermission[]);
    } catch {
      // silently fail — profile may not exist yet
    }
  }, [supabase]);

  const refreshProfile = useCallback(async () => {
    if (!user?.id) return;
    await loadProfileAndPermissions(user.id);
  }, [user, loadProfileAndPermissions]);

  useEffect(() => {
    // Get initial session
    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session);
      setUser(session?.user ?? null);
      if (session?.user?.id) {
        loadProfileAndPermissions(session.user.id).finally(() => setLoading(false));
      } else {
        setLoading(false);
      }
    });

    // Listen for auth changes
    const {
      data: { subscription }
    } = supabase.auth.onAuthStateChange((_event, session) => {
      setSession(session);
      setUser(session?.user ?? null);
      if (session?.user?.id) {
        loadProfileAndPermissions(session.user.id).finally(() => setLoading(false));
      } else {
        setProfile(null);
        setPermissions([]);
        setLoading(false);
      }
    });

    return () => subscription.unsubscribe();
  }, []);

  // Email/Password Sign Up
  const signUp = async (email: string, password: string, metadata = {}) => {
    const { data, error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        data: {
          full_name: (metadata as any)?.fullName || '',
          avatar_url: (metadata as any)?.avatarUrl || '',
          role: (metadata as any)?.role || 'agent',
          region_code: (metadata as any)?.regionCode || '',
        },
        emailRedirectTo: `${window.location.origin}/auth/callback`
      }
    });
    if (error) throw error;
    return data;
  };

  // Email/Password Sign In
  const signIn = async (email: string, password: string) => {
    const { data, error } = await supabase.auth.signInWithPassword({
      email,
      password
    });
    if (error) throw error;
    return data;
  };

  // Sign Out
  const signOut = async () => {
    const { error } = await supabase.auth.signOut();
    if (error) throw error;
    setProfile(null);
    setPermissions([]);
  };

  // Get Current User
  const getCurrentUser = async () => {
    const { data: { user }, error } = await supabase.auth.getUser();
    if (error) throw error;
    return user;
  };

  // Check if Email is Verified
  const isEmailVerified = () => {
    return user?.email_confirmed_at !== null;
  };

  // Get User Profile from Database
  const getUserProfile = async (): Promise<UserProfile | null> => {
    if (!user) return null;
    const { data, error } = await supabase
      .from('user_profiles')
      .select('*, region:regions(*)')
      .eq('id', user.id)
      .single();
    if (error) throw error;
    return data as UserProfile;
  };

  const userRole: UserRole | null = profile?.role ?? null;
  const userRegion: Region | null = (profile?.region as Region) ?? null;
  const isDirection = userRole === 'super_admin' || userRole === 'direction' || userRole === 'admin';

  const value: AuthContextType = {
    user,
    session,
    loading,
    profile,
    permissions,
    userRole,
    userRegion,
    isDirection,
    signUp,
    signIn,
    signOut,
    getCurrentUser,
    isEmailVerified,
    getUserProfile,
    refreshProfile,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
